<template>
  <article
    class="rounded-lg p-4 flex flex-col gap-2"
    :style="{ background: 'var(--bg-elev)', border: `1px solid ${borderColor}` }"
  >
    <p class="text-xs uppercase tracking-wider leading-snug" style="color: var(--fg-muted)">
      {{ label }}
    </p>
    <p class="text-2xl font-semibold leading-none">{{ displayValue }}</p>
    <footer class="flex items-end justify-between gap-2 mt-1" style="color: var(--fg-muted)">
      <Sparkline
        :series="series"
        :width="96"
        :height="24"
        :stroke="strokeColor"
        :aria-label="`${label} trend`"
      />
      <span class="text-xs whitespace-nowrap" :class="deltaClass">{{ deltaText }}</span>
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Sparkline from "./Sparkline.vue";
import type { RagBand } from "@/api/types";

const props = withDefaults(
  defineProps<{
    label: string;
    current: number | null;
    previous: number | null;
    delta: number | null;
    rag: RagBand;
    series: Array<number | null>;
    /** "pct" formats as percentage; "int" rounds to integer; "num" uses 1 decimal. */
    format?: "pct" | "int" | "num";
    /** "higher" means a positive delta is good; "lower" inverts the colour cue. */
    goodDirection?: "higher" | "lower";
  }>(),
  { format: "pct", goodDirection: "higher" },
);

const displayValue = computed(() => {
  if (props.current == null || Number.isNaN(props.current)) return "—";
  if (props.format === "pct") return `${(props.current * 100).toFixed(0)}%`;
  if (props.format === "int") return Math.round(props.current).toLocaleString();
  return props.current.toFixed(1);
});

const deltaText = computed(() => {
  if (props.delta == null || Number.isNaN(props.delta)) return "no Δ";
  const sign = props.delta > 0 ? "+" : "";
  if (props.format === "pct") return `${sign}${(props.delta * 100).toFixed(0)}pp vs prev`;
  if (props.format === "int") return `${sign}${Math.round(props.delta).toLocaleString()} vs prev`;
  return `${sign}${props.delta.toFixed(1)} vs prev`;
});

const deltaClass = computed(() => {
  if (props.delta == null || props.delta === 0) return "";
  const goodIsHigher = props.goodDirection === "higher";
  const isPositive = props.delta > 0;
  const isGood = goodIsHigher === isPositive;
  return isGood ? "text-emerald-500" : "text-rose-500";
});

const strokeColor = computed(() => {
  switch (props.rag) {
    case "green":
      return "#10b981";
    case "amber":
      return "#f59e0b";
    case "red":
      return "#ef4444";
    default:
      return "var(--fg-muted)";
  }
});

const borderColor = computed(() => {
  switch (props.rag) {
    case "green":
      return "rgba(16,185,129,.25)";
    case "amber":
      return "rgba(245,158,11,.30)";
    case "red":
      return "rgba(239,68,68,.30)";
    default:
      return "var(--border)";
  }
});
</script>
